import { AppForm } from '../../../../../../../common/forms/app.form';
import { Injectable } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  ApplicationAppEntityApplicationRead,
  ApplicationAppEntityApplicationWrite,
  LargeFamilyCardAppEntityLargeFamilyCardWrite
} from '../../../../../../../openapi';
import { toNumber } from '../../../../../../../services/helpers';
import { CustomValidator } from '../../../../../../../common/forms/custom.validators';
import { LargeFamilyForm } from './large-family.form';


@Injectable()
export class MunicipalWasteDisposalDeclarationSubjectForm extends AppForm {

  public validators = Validators;

  public submissionPurposeOptions: string[] = [
    'Powstanie obowiązku',
    'Zmiana danych w deklaracji',
    'Wygaśnięcie obowiązku',
    'Korekta deklaracji '
  ];

  public propertyTypeOptions: string[] = [
    'Całej nieruchomości',
    'Części nieruchomości'
  ];

  public entityKindOptions: string[] = [
    'Właściciel',
    'Użytkownik wieczysty',
    'Współwłaściciel',
    'Zarządca, zarząd wspólnoty mieszkaniowej, zarządca zasobów spółdzielczych',
    'Inny podmiot'
  ];

  public partialTypeExemptions: string[] = [
    'Karta dużej rodziny',
    'Kryterium dochodowe z art. 8 ust. 1 ustawy o pomocy społecznej',
    'Brak'
  ];

  constructor() {
    super({
      propertyType: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      submissionPurpose: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      startDate: new FormControl(null, { validators: [Validators.required, CustomValidator.isValidDateFormat()] }),
      entityKind: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),

      registrationNumber: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      city: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }),
      street: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      houseNumber: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }),
      apartmentNumber: new FormControl('', Validators.maxLength(255)),

      numberOfResidents: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      feeRate: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      feeAmount: new FormControl({ value: '0.00', disabled: false }, {
        nonNullable: true,
        validators: [Validators.maxLength(255)]
      }),

      identicalBuildingsCount: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(255)] }),
      propertyHasComposter: new FormControl(null, {
        validators: [Validators.required]
      }),

      partialTypeExemption: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      numberOfResidentsForExemption: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      exemptionAmountRate: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      exemptionAmount: new FormControl('0.00', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),

      totalMonthlyFeeAmount: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      })
    });
  }

  public get numberOfResidents(): FormControl {
    return this.get('numberOfResidents') as FormControl;
  }

  public numberOfResidentsChange(v: string): void {
    this.numberOfResidentsForBioExemption?.setValue(v, { emitEvent: false });
    this.numberOfResidentsForExemption?.setValue(v, { emitEvent: false });
  }

  public partialTypeExemptionChange(v: string): void {
    const numberOfResidentsForExemption: FormControl = this.get('numberOfResidentsForExemption') as FormControl;
    const exemptionAmountRate: FormControl = this.get('exemptionAmountRate') as FormControl;
    const exemptionAmount: FormControl = this.get('exemptionAmount') as FormControl;

    const validators = [Validators.required, Validators.maxLength(255)];
    if (v === 'Brak') {
      numberOfResidentsForExemption.setValidators([]);
      exemptionAmountRate.setValidators([]);
      exemptionAmount.setValidators([]);
    } else {
      numberOfResidentsForExemption.setValidators(validators);
      exemptionAmountRate.setValidators(validators);
      exemptionAmount.setValidators(validators);
    }
    numberOfResidentsForExemption.updateValueAndValidity({ emitEvent: false });
    exemptionAmountRate.updateValueAndValidity({ emitEvent: false });
    exemptionAmount.updateValueAndValidity({ emitEvent: false });

    if (v === 'Karta dużej rodziny') {
      this.addControl('largeFamilyForm', new LargeFamilyForm());
    } else {
      this.removeControl('largeFamilyForm');
    }
    if (v === 'Kryterium dochodowe z art. 8 ust. 1 ustawy o pomocy społecznej') {
      this.numberOfResidentsForExemption?.disable();
    } else {
      this.numberOfResidentsForExemption?.enable();
    }
  }

  public get largeFamilyForm(): LargeFamilyForm | null {
    return this.get('largeFamilyForm') as LargeFamilyForm | null;
  }

  public get feeAmount(): FormControl {
    return this.get('feeAmount') as FormControl;
  }

  public calcFeeAmount(): void {
    const numberOfResidents = +(this.value.numberOfResidents || '0');
    const feeRate = +(this.value.feeRate || '0');
    if (numberOfResidents && feeRate) {
      this.feeAmount.setValue((numberOfResidents * feeRate).toFixed(2));
    } else {
      this.feeAmount.setValue('0.00');
    }
    this.calcTotalMonthlyFeeAmount();
  }

  private get numberOfResidentsForExemption(): FormControl {
    return this.get('numberOfResidentsForExemption') as FormControl;
  }

  private get exemptionAmountRate(): FormControl {
    return this.get('exemptionAmountRate') as FormControl;
  }

  private get exemptionAmount(): FormControl {
    return this.get('exemptionAmount') as FormControl;
  }

  public calcExemptionAmount(): void {
    const numberOfResidentsForExemptionValue = toNumber(this.numberOfResidentsForExemption.value);
    const exemptionAmountRateValue = toNumber(this.exemptionAmountRate.value);
    this.exemptionAmount.setValue((numberOfResidentsForExemptionValue * exemptionAmountRateValue).toFixed(2));
    this.calcTotalMonthlyFeeAmount();
  }

  public propertyHasComposterChange(v: boolean): void {
    if (v) {
      this.addControl(
        'numberOfResidentsForBioExemption',
        new FormControl({ value: this.numberOfResidents.value, disabled: true }, {
          nonNullable: true,
          validators: [Validators.required]
        })
      );
      this.addControl(
        'bioExemptionRate',
        new FormControl('', {
          nonNullable: true,
          validators: [Validators.required]
        })
      );
      this.addControl(
        'bioExemptionAmount',
        new FormControl({
          value: '0.00',
          disabled: true
        }, {
          nonNullable: true,
          validators: [Validators.required]
        })
      );
    } else {
      this.removeControl('numberOfResidentsForBioExemption');
      this.removeControl('bioExemptionRate');
      this.removeControl('bioExemptionAmount');
    }
  }

  public calcBioExemptionAmount(): void {
    const numberOfResidentsForBioExemption = +(this.numberOfResidentsForBioExemption?.value || '0');
    const bioExemptionRate = +(this.bioExemptionRate?.value || '0');

    if (numberOfResidentsForBioExemption && bioExemptionRate) {
      this.bioExemptionAmount?.setValue((numberOfResidentsForBioExemption * bioExemptionRate).toFixed(2));
    } else {
      this.bioExemptionAmount?.setValue('0.00');
    }
    this.calcTotalMonthlyFeeAmount();
  }

  public get numberOfResidentsForBioExemption(): FormControl | null {
    return this.get('numberOfResidentsForBioExemption') as FormControl | null;
  }

  public get bioExemptionRate(): FormControl | null {
    return this.get('bioExemptionRate') as FormControl | null;
  }

  public get bioExemptionAmount(): FormControl | null {
    return this.get('bioExemptionAmount') as FormControl | null;
  }

  public get totalMonthlyFeeAmount(): FormControl {
    return this.get('totalMonthlyFeeAmount') as FormControl;
  }

  public calcTotalMonthlyFeeAmount(): void {
    const feeAmount = +(this.feeAmount.value || '0');
    const exemptionAmount = +(this.exemptionAmount.value || '0');
    const bioExemptionAmount = +(this.bioExemptionAmount?.value || '0');
    this.totalMonthlyFeeAmount.setValue(Math.abs(feeAmount - exemptionAmount - bioExemptionAmount).toFixed(2));
  }

  public get entityKind(): FormControl {
    return this.get('entityKind') as FormControl;
  }

  public get propertyType(): FormControl {
    return this.get('propertyType') as FormControl;
  }

  public get submissionPurpose(): FormControl {
    return this.get('submissionPurpose') as FormControl;
  }

  public get propertyHasComposter(): FormControl {
    return this.get('propertyHasComposter') as FormControl;
  }

  public override patchFormValues(value: ApplicationAppEntityApplicationRead): void {
    this.propertyHasComposterChange(!!value?.propertyHasComposter);
    this.propertyType.patchValue(value?.propertyType);
    this.submissionPurpose.setValue(value?.submissionPurpose || '');
    this.entityKind.setValue(value?.entityKind || '');

    this.partialTypeExemptionChange(value?.partialTypeExemption || '');

    super.patchFormValues({
      ...value
    });
    this.calcTotalMonthlyFeeAmount();
    this.largeFamilyForm?.patchFormValues(value);
  }

  public get cards(): LargeFamilyCardAppEntityLargeFamilyCardWrite[] {
    return this.largeFamilyForm?.cards?.value || [];
  }

  public toWriteModel(): ApplicationAppEntityApplicationWrite {
    return {
      ...this.value,
      ...this.largeFamilyForm?.toWriteModel(),
      partialTypeExemption: this.value.partialTypeExemption,
      propertyHasComposter: this.propertyHasComposter.value,
      numberOfResidents: toNumber(this.value.numberOfResidents),
      feeRate: toNumber(this.value.feeRate),
      feeAmount: toNumber(this.value.feeAmount),
      numberOfResidentsForBioExemption: this.numberOfResidentsForBioExemption ? toNumber(this.numberOfResidentsForBioExemption.value) : null,
      bioExemptionRate: this.bioExemptionRate ? toNumber(this.bioExemptionRate.value) : null,
      bioExemptionAmount: this.bioExemptionAmount ? toNumber(this.bioExemptionAmount.value) : null,
      identicalBuildingsCount: toNumber(this.value.identicalBuildingsCount),
      numberOfResidentsForExemption: toNumber(this.numberOfResidentsForExemption.value),
      exemptionAmountRate: toNumber(this.value.exemptionAmountRate),
      exemptionAmount: toNumber(this.value.exemptionAmount),
      totalMonthlyFeeAmount: toNumber(this.totalMonthlyFeeAmount.value)
    };
  }
}
